// On-device cache of the last known StoreKit entitlement.
//
// Why it exists: RevenueCat's getCustomerInfo() can fail outright (no network on
// a cold start, RevenueCat having a bad day). Without a cache the honest answer
// in that moment is "unknown", and "unknown" on the paywall side of the app
// looks exactly like "you have not paid" to a customer who has. This lets the
// app say "you paid, we just cannot reach the server right now".
//
// It is deliberately conservative:
//   - stamped with the Supabase uid, so a second account on the same device
//     cannot inherit the first account's access (the same rule the existing
//     `ttt_has_plan_email` owner stamp enforces for the web fallback),
//   - only ever consulted when the live call FAILED, never in preference to it,
//   - expiry-bounded, with the same 3-day grace a Stripe row already gets.
//
// Storage: the Capacitor Preferences plugin if the bridge has it registered
// (durable native storage that survives a webview data clear), otherwise
// localStorage. Preferences is reached through the bridge global rather than an
// import so this module adds no package dependency and no web bundle weight,
// and so it cannot break the build before that plugin is installed.

import { hasNativePlugin } from '@/lib/platform';
import { ENTITLEMENT_CACHE_KEY, CACHE_GRACE_MS } from './config';

function preferencesPlugin() {
  try {
    if (!hasNativePlugin('Preferences')) return null;
    return window.Capacitor.Plugins.Preferences;
  } catch {
    return null;
  }
}

async function readRaw() {
  const prefs = preferencesPlugin();
  if (prefs) {
    try {
      const { value } = await prefs.get({ key: ENTITLEMENT_CACHE_KEY });
      if (value) return value;
    } catch {
      /* fall through to localStorage */
    }
  }
  try {
    return localStorage.getItem(ENTITLEMENT_CACHE_KEY);
  } catch {
    return null;
  }
}

async function writeRaw(value) {
  const prefs = preferencesPlugin();
  if (prefs) {
    try {
      await prefs.set({ key: ENTITLEMENT_CACHE_KEY, value });
    } catch {
      /* fall through, localStorage below is still written */
    }
  }
  try {
    localStorage.setItem(ENTITLEMENT_CACHE_KEY, value);
  } catch {
    /* private mode or storage full: the cache is an optimisation, not a contract */
  }
}

/**
 * Remember the entitlement we just read from RevenueCat.
 * @param {{ userId: string|null, active: boolean, expiresAtMs: number|null }} snapshot
 */
export async function rememberEntitlement(snapshot) {
  try {
    await writeRaw(
      JSON.stringify({
        userId: snapshot?.userId || null,
        active: snapshot?.active === true,
        expiresAtMs: Number.isFinite(snapshot?.expiresAtMs) ? snapshot.expiresAtMs : null,
        at: Date.now(),
      })
    );
  } catch {
    /* non-fatal by design */
  }
}

/**
 * The cached entitlement, but only if it can still be trusted for this user.
 *
 * Returns null when there is nothing usable, which callers must treat as
 * "unknown", never as "not subscribed".
 *
 * @param {string|null} userId current Supabase auth uid
 * @returns {Promise<{ active: boolean, expiresAtMs: number|null, source: 'cache' }|null>}
 */
export async function readCachedEntitlement(userId) {
  try {
    const raw = await readRaw();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.active !== true) return null;

    // Owner stamp. A cache written for someone else is not evidence about the
    // person signed in now. A cache with no stamp is legacy and not trusted.
    if (!parsed.userId || (userId && parsed.userId !== userId)) return null;

    // A null expiry means lifetime access, which this product does not sell, so
    // treat it as untrusted rather than as forever.
    if (!Number.isFinite(parsed.expiresAtMs)) return null;
    if (parsed.expiresAtMs + CACHE_GRACE_MS <= Date.now()) return null;

    return { active: true, expiresAtMs: parsed.expiresAtMs, source: 'cache' };
  } catch {
    return null;
  }
}

/** Drop the cache. Called on sign out, so the next account starts clean. */
export async function forgetEntitlement() {
  const prefs = preferencesPlugin();
  if (prefs) {
    try {
      await prefs.remove({ key: ENTITLEMENT_CACHE_KEY });
    } catch {
      /* non-fatal */
    }
  }
  try {
    localStorage.removeItem(ENTITLEMENT_CACHE_KEY);
  } catch {
    /* non-fatal */
  }
}
